import React from "react";
import { ExportButton } from "@/components/ExportButton";
import TallyExportButton from "@/components/TallyExportButton";
import { isFmbTallyConfigReady } from "@/types/tally-config";
import {
  Datagrid,
  List,
  TextField,
  FunctionField,
  Button,
  ReferenceField,
  TextInput,
  DateInput,
  SelectInput,
  TopToolbar,
  CreateButton,
} from "react-admin";
import type { Exporter, Identifier, RaRecord } from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import { formatINR } from "@/utils";
import { formatListDate } from "@/utils/date-format";
import { exportToExcel } from "@/utils/export-to-excel";
// import { FmbReceiptCsvImportButton } from "./FmbReceiptCsvImportButton";
import FmbReceiptPaymentSummary from "./FmbReceiptPaymentSummary";
import { FMB_RECEIPT_FILTER_FIELDS } from "./fmb-receipt-filter-fields";
import {
  FMB_RECEIPT_EXPORT_COLUMNS,
  FMB_RECEIPT_EXPORT_OPTIONS,
  fmbReceiptTargetLabel,
  type FmbReceiptListRow,
} from "./fmb-receipt-list-helpers";

const PAYMENT_MODE_CHOICES = [
  { id: "CASH", name: "CASH" },
  { id: "ONLINE", name: "ONLINE" },
  { id: "CHEQUE", name: "CHEQUE" },
];

const [searchField, paymentModeField, startDateField, endDateField] = FMB_RECEIPT_FILTER_FIELDS;

const ListActions = () => (
  <TopToolbar>
    {/* <FmbReceiptCsvImportButton /> */}
    <CreateButton />
    <ExportButton />
    <TallyExportButton
      module="fmb"
      filenamePrefix="fmb-tally"
      isConfigReady={isFmbTallyConfigReady}
    />
  </TopToolbar>
);

const exporter = (records: RaRecord[]) =>
  exportToExcel(
    FMB_RECEIPT_EXPORT_COLUMNS,
    records as FmbReceiptListRow[],
    FMB_RECEIPT_EXPORT_OPTIONS
  );

export default function FmbReceiptList() {
  const printReceipt = (id: string | number) => {
    window.open(`#/fmb-receipt?receiptId=${id}`, "_blank");
  };
  const ReceiptFilters = [
    <TextInput
      key="q"
      source={searchField.source}
      label="Search (receipt no., ITS, amounts, date, type, mode, remarks…)"
      alwaysOn
      sx={{ minWidth: 300 }}
    />,
    <SelectInput
      key="paymentMode"
      source={paymentModeField.source}
      label={paymentModeField.label}
      choices={PAYMENT_MODE_CHOICES}
      emptyText="All modes"
      alwaysOn
      sx={{ minWidth: 140 }}
    />,
    <DateInput
      key="startDate"
      source={startDateField.source}
      label={startDateField.label}
      alwaysOn
    />,
    <DateInput key="endDate" source={endDateField.source} label={endDateField.label} alwaysOn />,
  ];
  return (
    <List
      sort={{ field: "receiptNo", order: "DESC" }}
      filters={ReceiptFilters}
      actions={<ListActions />}
      exporter={exporter as unknown as Exporter<RaRecord<Identifier>>}
      empty={false}
    >
      <FmbReceiptPaymentSummary />
      <Datagrid rowClick="show">
        <TextField source="receiptNo" />
        <ReferenceField
          source="fmbId"
          label="HOF ITS"
          reference="fmbData"
          link="show"
          sortable={false}
        >
          <TextField source="itsNo" />
        </ReferenceField>
        <FunctionField
          label="Amount"
          textAlign="right"
          sortBy="amount"
          render={(record) => formatINR(record?.amount, { empty: "—" })}
        />
        <FunctionField
          label="Credit used"
          textAlign="right"
          sortBy="creditUsed"
          render={(record) => formatINR(record?.creditUsed ?? 0, { empty: "—" })}
        />
        <FunctionField
          label="Receipt date"
          sortBy="receiptDate"
          render={(record: RaRecord) => formatListDate(record?.receiptDate, { empty: "—" })}
        />
        <FunctionField
          label="Target"
          sortable={false}
          render={(record) => fmbReceiptTargetLabel(record as FmbReceiptListRow)}
        />
        <TextField source="receiptType" />
        <TextField source="remarks" label="Remarks" emptyText="—" />
        <FunctionField
          label="Download"
          source="formNo"
          sortable={false}
          render={(record) => (
            <Button onClick={() => printReceipt(record.id)}>
              <DownloadIcon />
            </Button>
          )}
          key="name"
        />
      </Datagrid>
    </List>
  );
}
